import { useGetExerciseTypes } from '#api/exercise-types';
import { useExercisePresetChartRecoil } from '#states/exercisePresetsState';
import { IExercisePreset } from '#types/exercisePresets';

import { produce } from 'immer';
import { useState } from 'react';
import styled from 'styled-components';
import { PresetChange } from './PresetChange';

interface PresetItemProps extends IExercisePreset {}
export const PresetItem = (props: PresetItemProps) => {
  const [checked, setChecked] = useState<boolean>(false);

  const { data } = useGetExerciseTypes();

  const { setExercisePresetChart } = useExercisePresetChartRecoil();

  // weight는 string으로 들어오기 때문에 number로 변환해서 계산
  const convertedProps = {
    ...props,
    exercisePresetWorkout: props.exercisePresetWorkout.map(workout => ({
      ...workout,
      sets: workout.sets.map(set => ({
        ...set,
        weight: Number(set.weight),
        reps: Number(set.reps),
        time: Number(set.time)
      }))
    }))
  };

  const onClick = () => {
    if (checked) {
      setExercisePresetChart(
        produce(draft => {
          const index = draft.findIndex(item => item.id === props.id);
          draft.splice(index, 1);
        })
      );
    } else {
      setExercisePresetChart(
        produce(draft => {
          draft.push(convertedProps);
        })
      );
    }

    setChecked(prev => !prev);
  };

  return (
    <StyledItem
      style={{
        borderColor: checked
          ? 'var(--Brand-400, #7750E9)'
          : 'var(--bg-500, #373245)'
      }}
      checked={checked}
      onClick={onClick}
    >
      <div className="flex flex-col gap-12 w-full">
        <div className="w-full flex justify-between items-center">
          <div className="text-17 font-500 text-[var(--Gray-Scale-400, #BCB8C3)]">
            {props.name}
          </div>
          <PresetChange id={props.id} />
        </div>
        {props.exercisePresetWorkout.map((workout, index) => {
          const currentExerciseType = data?.data.find(
            item => item.id === workout.exerciseTypeId
          );

          const name = currentExerciseType?.name;

          const part = currentExerciseType?.part;

          const totalSet = workout.sets.length;

          const totalWeight = workout.sets.reduce(
            (acc, cur) =>
              acc + (Number(cur.weight) || 0) * (Number(cur.reps) || 0),
            0
          );

          // 최고 회수
          const maxReps = Math.max(
            ...workout.sets.map(set => Number(set.reps) || 0)
          );

          // 최고 시간
          const maxTime = Math.max(
            ...workout.sets.map(set => Number(set.time) || 0)
          );

          // 최고 중량
          const maxWeight = Math.max(
            ...workout.sets.map(set => Number(set.weight) || 0)
          );

          // 최고 중량인 세트를 찾아서 무게 * 시간 형식으로 반환
          const repsWeight = workout.sets
            .filter(set => Number(set.weight) === maxWeight)
            .map(set => `${set.weight}kg x ${set.reps}회`)
            .join(' / ');

          let total = '';

          switch (workout.sets[0].setType) {
            case 'REP':
              total = `총 ${totalSet}세트, ${maxReps}회`;
              break;
            case 'REPWEIGHT':
              total = `총 ${totalSet}세트, ${repsWeight}, 총 ${totalWeight.toLocaleString(
                'ko-KR'
              )}kg`;
              break;
            case 'REPTIME':
              total = `총 ${totalSet}세트,${maxTime}초, ${maxReps}회`;
              break;
            case 'TIME':
              total = `총 ${totalSet}세트, ${maxTime}초`;
          }

          return (
            <div className="flex gap-8" key={index}>
              <div
                style={{
                  display: 'flex',
                  width: '20px',
                  height: '20px',
                  padding: '4px 8px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  borderRadius: '4px',
                  background: 'var(--bg-500, #373245)',
                  marginBottom: '8px'
                }}
              >
                <div
                  style={{
                    color: 'var(--Gray-Scale-500, #948F9E)',
                    textAlign: 'center',
                    fontFamily: '"Pretendard Variable"',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '16px'
                  }}
                >
                  {index + 1}
                </div>
              </div>
              <div className="flex flex-col gap-8">
                <div className="flex gap-10">
                  <div className="text-[#e7e3ee] text-base font-normal font-['Pretendard Variable']">
                    {name}
                  </div>
                  <div className="text-[#9170f2] text-sm font-normal font-['Pretendard Variable']">
                    {part}
                  </div>
                </div>
                <div className="text-[#746f7e] text-sm font-normal font-['Pretendard Variable']">
                  {total}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </StyledItem>
  );
};

const StyledItem = styled.div<{ checked: boolean }>`
  display: flex;
  padding: 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;

  border-radius: 12px;
  border: 1px solid
    ${checked =>
      checked ? 'var(--Brand-400, #7750E9)' : 'var(--bg-500, #373245)'};
  background: var(--bg-600, #2e2a3a);
`;
